import React from 'react';
import type { JSONContent } from '@tiptap/react';
import { Search, MessageSquare } from 'lucide-react';
import { Input } from '../ui/Input';
import { ConversationPanel, EmptyConversationPanel, type ThreadMessage, type ThreadRead } from './ConversationPanel';
import type { ThreadSummary } from './MessageThreadList';

// Messagerie façon boîte de réception (liste à gauche + barre de recherche, conversation ouverte à droite)
// — remplace la version en modale suite au retour explicite d'Emeni (2026-08-19) : "je préfère que ça
// n'ouvre pas de modale, à gauche les messages reçus/envoyés avec une barre de recherche, à droite la
// conversation". Partagé entre TeamMessagingSettings.tsx (établissement) et EmployeeMessages.tsx (salarié).
export function MessagingSplitView({
    threads, selectedThreadId, onSelectThread, subtitle, emptyListLabel, headerExtra,
    conversationTitle, conversationSubtitle, messages, threadLoading, myAuthorType,
    onReply, sending, onViewAttachment, viewingAttachmentId, emptyConversationLabel,
    otherReads, recipientCount, allowReplies, onToggleAllowReplies,
}: {
    threads: ThreadSummary[];
    selectedThreadId: string | null;
    onSelectThread: (id: string) => void;
    subtitle: (t: ThreadSummary) => React.ReactNode;
    emptyListLabel: string;
    headerExtra?: React.ReactNode;
    conversationTitle: string;
    conversationSubtitle?: string;
    messages: ThreadMessage[];
    threadLoading: boolean;
    myAuthorType: 'admin' | 'employe';
    onReply: (payload: { text: string; rich: JSONContent; attachment: File | null }) => Promise<void>;
    sending: boolean;
    onViewAttachment: (messageId: string, win: Window | null) => void;
    viewingAttachmentId: string | null;
    emptyConversationLabel: string;
    otherReads?: ThreadRead[];
    recipientCount?: number;
    allowReplies?: boolean;
    onToggleAllowReplies?: (next: boolean) => void;
}) {
    const [search, setSearch] = React.useState('');

    const filteredThreads = React.useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return threads;
        return threads.filter((t) => t.title.toLowerCase().includes(q) || (t.last_message || '').toLowerCase().includes(q));
    }, [threads, search]);

    return (
        <div className="flex border border-border rounded-2xl overflow-hidden bg-surface h-[calc(100vh-220px)] min-h-[480px]">
            <div className="w-[320px] shrink-0 border-r border-border flex flex-col">
                <div className="p-3 border-b border-border space-y-2 shrink-0">
                    {headerExtra}
                    <div className="relative">
                        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-content-muted" />
                        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher..." className="pl-8 h-8 text-xs" />
                    </div>
                </div>
                <div className="flex-1 overflow-y-auto">
                    {filteredThreads.length === 0 ? (
                        <div className="text-center py-12 px-4">
                            <MessageSquare size={24} className="mx-auto mb-2 text-content-muted opacity-30" />
                            <p className="text-xs text-content-muted">{emptyListLabel}</p>
                        </div>
                    ) : filteredThreads.map((t) => (
                        <button key={t.id} onClick={() => onSelectThread(t.id)}
                            className={`w-full text-left px-3.5 py-3 border-b border-border/60 transition-colors ${selectedThreadId === t.id ? 'bg-brand-600/10' : 'hover:bg-surface-soft'}`}>
                            <div className="flex items-start justify-between gap-2">
                                <p className="font-bold text-content text-xs flex items-center gap-1.5 min-w-0 truncate">
                                    {t.title}
                                    {!!t.is_unread && <span className="w-1.5 h-1.5 rounded-full bg-red-500 shrink-0" />}
                                </p>
                                <span className="shrink-0 text-[10px] text-content-muted">
                                    {new Date(t.last_message_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
                                </span>
                            </div>
                            <p className="text-[10px] text-content-muted mt-0.5 flex items-center gap-1">{subtitle(t)}</p>
                            <p className="text-[11px] text-content mt-1 truncate">{t.last_message}</p>
                        </button>
                    ))}
                </div>
            </div>
            <div className="flex-1 min-w-0">
                {selectedThreadId ? (
                    <ConversationPanel
                        title={conversationTitle}
                        subtitle={conversationSubtitle}
                        messages={messages}
                        loading={threadLoading}
                        myAuthorType={myAuthorType}
                        onReply={onReply}
                        sending={sending}
                        onViewAttachment={onViewAttachment}
                        viewingAttachmentId={viewingAttachmentId}
                        otherReads={otherReads}
                        recipientCount={recipientCount}
                        allowReplies={allowReplies}
                        onToggleAllowReplies={onToggleAllowReplies}
                    />
                ) : (
                    <EmptyConversationPanel label={emptyConversationLabel} />
                )}
            </div>
        </div>
    );
}
